import { ILocationItem } from 'components_new/molecules/LocationItem'

import { EAccountRole, TSignedInAccount } from 'types/GlobalAccount'
import { DashboardOwnership, TDashboardGroup } from 'types/GlobalDashboardGroup'

import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { useAvailableDashboardGroups } from 'redux/hooks/DashboardGroups'
import { useDashboard, useDashboards } from 'redux/hooks/Dashboards'
import { TDashboard } from 'types/GlobalDashboard'
import { useAvailableDashboardFolders } from 'redux/hooks/DashboardFolders'

export type SelectType = 'folder' | 'dashboard' | 'tab'

function parseDashboardGroup(
  group: TDashboardGroup,
  dashboardsInGroup: TDashboard[],
  accountRole: EAccountRole,
  type: 'dashboard' | 'tab'
): ILocationItem {
  // check users permission to edit dashboard
  const writeAccess =
    accountRole === EAccountRole.ADMIN ||
    group.ownership === DashboardOwnership.OWNED_BY_ME

  if (type === 'dashboard') {
    return {
      allow: true,
      disabled: !writeAccess,
      disabledHint: 'Du saknar rättigheter',
      id: group.id,
      name: group.title,
      iconName: 'AnalyticsOutlined' as const
    }
  }

  const disabledHint = !writeAccess
    ? 'Du saknar rättigheter'
    : 'Dashboard är full'

  const onlyOneTab = dashboardsInGroup.length === 1

  const groupDisabled =
    !writeAccess || (onlyOneTab && !dashboardsInGroup[0].available_space)

  return {
    allow: onlyOneTab,
    disabled: groupDisabled,
    disabledHint,
    // if only one tab, then use its id.
    id: onlyOneTab ? dashboardsInGroup[0].id : group.id,
    name: group.title,
    iconName: 'AnalyticsOutlined' as const,
    // if more than 1 tab then user has to select tab.
    subItems: !onlyOneTab
      ? dashboardsInGroup.map((dashboard) => ({
        allow: true,
        disabled: !dashboard.available_space,
        disabledHint: disabledHint,
        id: dashboard.id,
        name: dashboard.title,
        iconName: 'TabUnselectedOutlined' as const
      }))
      : undefined
  }
}

export function useOptions(
  user: TSignedInAccount | null,
  type: SelectType,
  dashboardId?: string
): ILocationItem[] {
  const dashboardGroups = useAvailableDashboardGroups()
  const dashboard = useDashboard(dashboardId || null)
  const allDashboards = useDashboards()
  const folders = useAvailableDashboardFolders()

  // Create a mapper: dashboard_group_id -> sorted dashboards
  const dashboardsByGroup: Record<string, TDashboard[]> = {}

  allDashboards.forEach((dash) => {
    if (!dashboardsByGroup[dash.dashboard_group_id]) {
      dashboardsByGroup[dash.dashboard_group_id] = []
    }
    dashboardsByGroup[dash.dashboard_group_id].push(dash)
  })

  // Sort dashboards in each group by index
  Object.keys(dashboardsByGroup).forEach((groupId) => {
    dashboardsByGroup[groupId].sort((a, b) => a.index - b.index)
  })

  let availableGroups = [...dashboardGroups]

  if (type === 'dashboard') {
    availableGroups = availableGroups.filter((group) => {
      // if we are in a dashboard page,
      // then we want to remove the group from the list.
      return dashboard ? group.id !== dashboard.dashboard_group_id : true
    })
  }

  if (type === 'folder') {
    return folders.map((folder) => ({
      ...folder,
      allow: true,
      iconName: 'Folder'
    }))
  }
  const accountRole = user?.role ?? EAccountRole.VIEW

  const dashboardsWithoutFolder = availableGroups
    .filter((item) => !item.folder_id)
    .map((group) =>
      parseDashboardGroup(
        group,
        dashboardsByGroup[group.id] || [],
        accountRole,
        type
      )
    )
    .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))

  return [
    ...folders.map((folder) => ({
      allow: false,
      disabled: false,
      iconName: 'Folder' as const,
      id: folder.id,
      name: folder.name,
      subItems: availableGroups
        .filter((group) => group.folder_id === folder.id)
        .map((group) =>
          parseDashboardGroup(
            group,
            dashboardsByGroup[group.id] || [],
            accountRole,
            type
          )
        )
    })),
    ...dashboardsWithoutFolder
  ]
}
